import React, {useMemo, useState} from 'react';
import {Link} from 'react-router-dom';
import '../Games.css';
import {Player} from '../common/types';
import {loadJSON, saveJSON} from '../common/storage';
import {uid} from '../common/random';
import PlayerSetup from '../common/PlayerSetup';
import SingleReveal from '../common/SingleReveal';
import {BASE_RIDDLES, Riddle} from './riddles';

const MIN_PLAYERS = 3;
const PLAYERS_KEY = 'games_players';
const CUSTOM_RIDDLES_KEY = 'riddles_custom';
const DISABLED_RIDDLES_KEY = 'riddles_disabled';

type Stage = 'setup' | 'handoff' | 'discussing' | 'solved';

const RiddlesGame: React.FC = () => {
    const [players, setPlayers] = useState<Player[]>(() => loadJSON(PLAYERS_KEY, []));
    const [customRiddles, setCustomRiddles] = useState<Riddle[]>(() => loadJSON(CUSTOM_RIDDLES_KEY, []));
    const [disabledIds, setDisabledIds] = useState<string[]>(() => loadJSON(DISABLED_RIDDLES_KEY, []));
    const [newQuestion, setNewQuestion] = useState('');
    const [newAnswer, setNewAnswer] = useState('');

    const [stage, setStage] = useState<Stage>('setup');
    const [hostIndex, setHostIndex] = useState(0);
    const [currentRiddle, setCurrentRiddle] = useState<Riddle | null>(null);
    const [usedRiddles, setUsedRiddles] = useState<Set<string>>(new Set());

    const allRiddles = useMemo(() => [...BASE_RIDDLES, ...customRiddles], [customRiddles]);
    const activeRiddles = useMemo(
        () => allRiddles.filter(r => !disabledIds.includes(r.id)),
        [allRiddles, disabledIds]
    );

    const updatePlayers = (next: Player[]) => {
        setPlayers(next);
        saveJSON(PLAYERS_KEY, next);
    };

    const toggleRiddle = (id: string) => {
        const next = disabledIds.includes(id) ? disabledIds.filter(x => x !== id) : [...disabledIds, id];
        setDisabledIds(next);
        saveJSON(DISABLED_RIDDLES_KEY, next);
    };

    const addCustomRiddle = () => {
        const question = newQuestion.trim();
        const answer = newAnswer.trim();
        if (!question || !answer) return;
        const next = [...customRiddles, {id: uid(), question, answer, custom: true}];
        setCustomRiddles(next);
        saveJSON(CUSTOM_RIDDLES_KEY, next);
        setNewQuestion('');
        setNewAnswer('');
    };

    const removeCustomRiddle = (id: string) => {
        const next = customRiddles.filter(r => r.id !== id);
        setCustomRiddles(next);
        saveJSON(CUSTOM_RIDDLES_KEY, next);
    };

    const pickRiddle = (pool: Riddle[], used: Set<string>): {riddle: Riddle; nextUsed: Set<string>} => {
        let available = pool.filter(r => !used.has(r.id));
        let nextUsed = used;
        if (available.length === 0) {
            available = pool;
            nextUsed = new Set();
        }
        const riddle = available[Math.floor(Math.random() * available.length)];
        const updatedUsed = new Set(nextUsed);
        updatedUsed.add(riddle.id);
        return {riddle, nextUsed: updatedUsed};
    };

    const startGame = () => {
        if (players.length < MIN_PLAYERS || activeRiddles.length === 0) return;
        const {riddle, nextUsed} = pickRiddle(activeRiddles, new Set());
        setHostIndex(0);
        setCurrentRiddle(riddle);
        setUsedRiddles(nextUsed);
        setStage('handoff');
    };

    const nextRiddle = () => {
        const {riddle, nextUsed} = pickRiddle(activeRiddles, usedRiddles);
        setCurrentRiddle(riddle);
        setUsedRiddles(nextUsed);
        setHostIndex(i => (i + 1) % players.length);
        setStage('handoff');
    };

    const backToSetup = () => {
        setStage('setup');
        setCurrentRiddle(null);
    };

    return (
        <div className={`GmPage ${stage === 'setup' ? '' : 'GmPage--noBar'}`}>
            <div className="GmContainer">
                <div className="GmTopBar">
                    <Link to="/games" className="GmBack">← Игры</Link>
                    <h1 className="GmTitle" style={{margin: 0}}>Данетки</h1>
                </div>

                {stage === 'setup' && (
                    <>
                        <p className="GmSubtitle">
                            Один игрок втайне от всех узнаёт загадку и её разгадку, зачитывает условие вслух,
                            а остальные пытаются раскрыть тайну, задавая вопросы, на которые можно ответить
                            только «да», «нет» или «не важно».
                        </p>

                        <PlayerSetup players={players} setPlayers={updatePlayers} minPlayers={MIN_PLAYERS}/>

                        <div className="GmCard">
                            <h3 className="GmCard-title">Загадки ({activeRiddles.length} активно)</h3>
                            <div className="GmChecklist">
                                {allRiddles.map(riddle => (
                                    <label className="GmCheckItem" key={riddle.id}>
                                        <input
                                            type="checkbox"
                                            checked={!disabledIds.includes(riddle.id)}
                                            onChange={() => toggleRiddle(riddle.id)}
                                        />
                                        <span>{riddle.question}</span>
                                        {riddle.custom && <span className="GmCheckItem-custom">своя</span>}
                                        {riddle.custom && (
                                            <button
                                                className="GmIconBtn"
                                                style={{marginLeft: 'auto'}}
                                                onClick={() => removeCustomRiddle(riddle.id)}
                                                aria-label="Удалить"
                                            >✕</button>
                                        )}
                                    </label>
                                ))}
                            </div>
                            <div className="GmInputRow" style={{flexDirection: 'column', gap: 8}}>
                                <input
                                    className="GmInput"
                                    placeholder="Условие загадки"
                                    value={newQuestion}
                                    onChange={(e) => setNewQuestion(e.target.value)}
                                />
                                <input
                                    className="GmInput"
                                    placeholder="Разгадка"
                                    value={newAnswer}
                                    onChange={(e) => setNewAnswer(e.target.value)}
                                />
                                <button className="GmBtn" onClick={addCustomRiddle}>Добавить загадку</button>
                            </div>
                        </div>

                        <div className="GmBottomBar">
                            <div className="GmBottomBar-inner">
                                <button
                                    className="GmBtn GmBtn--primary GmBtn--big"
                                    disabled={players.length < MIN_PLAYERS || activeRiddles.length === 0}
                                    onClick={startGame}
                                >
                                    Начать игру
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {stage === 'handoff' && currentRiddle && (
                    <SingleReveal
                        player={players[hostIndex]}
                        handoffLabel="Ведущим этого раунда будет"
                        revealButtonLabel={`Я — ${players[hostIndex].name}, посмотреть загадку и ответ`}
                        hideButtonLabel="Спрятать и зачитать вслух"
                        warning="⚠ Не показывайте ответ остальным!"
                        onHide={() => setStage('discussing')}
                        content={
                            <>
                                <p className="GmSituationText">{currentRiddle.question}</p>
                                <p className="GmHint">Ответ (только для тебя):</p>
                                <p className="GmRoleName">{currentRiddle.answer}</p>
                            </>
                        }
                    />
                )}

                {stage === 'discussing' && currentRiddle && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Ведущий: {players[hostIndex].name}</h3>
                        <p className="GmSituationText">{currentRiddle.question}</p>
                        <p className="GmHint" style={{marginTop: 12}}>
                            Остальные задают вопросы, на которые можно ответить «да», «нет» или «не важно».
                        </p>
                        <div style={{marginTop: 18}}>
                            <button className="GmBtn GmBtn--primary GmBtn--big" onClick={() => setStage('solved')}>
                                Раскрыть ответ
                            </button>
                        </div>
                    </div>
                )}

                {stage === 'solved' && currentRiddle && (
                    <div className="GmCard">
                        <h3 className="GmCard-title">Разгадка</h3>
                        <p className="GmSituationText" style={{marginBottom: 12}}>{currentRiddle.question}</p>
                        <p><strong>{currentRiddle.answer}</strong></p>
                        <div className="GmBtnRow" style={{marginTop: 16}}>
                            <button className="GmBtn GmBtn--primary" onClick={nextRiddle}>Следующая загадка</button>
                            <button className="GmBtn" onClick={backToSetup}>Изменить игроков</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RiddlesGame;
